import React, { useEffect, useState, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, Clock } from 'lucide-react';
interface UpsellPopupProps {
  isOpen: boolean;
  onClose: () => void;
}
type Step = 'upsell1' | 'upsell2';
const UPSELL_IMG = 'https://cdn.shopify.com/s/files/1/0981/0781/8286/files/Libro_dorado_comprimida.png?v=1775619938';
const DOWNSELL_IMG = 'https://cdn.shopify.com/s/files/1/0981/0781/8286/files/Libro_negro_con_el_numero_888_comprimida.png?v=1775620039';
export function UpsellPopup({
  isOpen,
  onClose
}: UpsellPopupProps) {
  const [step, setStep] = useState<Step>('upsell1');
  const [timeLeft, setTimeLeft] = useState(300);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  // Preload both images when popup opens
  useEffect(() => {
    if (isOpen && !imagesLoaded) {
      const img1 = new Image();
      const img2 = new Image();
      let loaded = 0;
      const onLoad = () => {
        loaded++;
        if (loaded >= 2) setImagesLoaded(true);
      };
      img1.onload = onLoad;
      img2.onload = onLoad;
      img1.src = UPSELL_IMG;
      img2.src = DOWNSELL_IMG;
    }
  }, [isOpen, imagesLoaded]);
  useEffect(() => {
    if (isOpen) {
      setStep('upsell1');
      setTimeLeft(300);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  useEffect(() => {
    if (!isOpen) return;
    const timer = setInterval(() => {
      setTimeLeft(prev => prev > 0 ? prev - 1 : 0);
    }, 1000);
    return () => clearInterval(timer);
  }, [isOpen]);
  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };
  const trackAndRedirect = (url: string, contentId: string, value: number) => {
    try {
      if (typeof window !== 'undefined' && (window as any).fbq) {
        ;
        (window as any).fbq('track', 'AddToCart', {
          content_ids: [contentId],
          content_type: 'product',
          value,
          currency: 'USD'
        });
      }
    } catch (e) {
      console.warn('Facebook Pixel tracking failed:', e);
    }
    window.location.href = url;
  };
  const handleAccept1 = () => {
    trackAndRedirect('https://www.betterdailyguides.com/cart/51937703297326:1', '51937703297326', 12.78);
  };
  const handleDecline1 = () => setStep('upsell2');
  const handleAccept2 = () => {
    trackAndRedirect('https://www.betterdailyguides.com/cart/51937708212526:1', '51937708212526', 10.86);
  };
  const handleDecline2 = () => {
    trackAndRedirect('https://www.betterdailyguides.com/cart/51934295621934:1', '51934295621934', 6.39);
  };
  if (!isOpen) return null;
  return <AnimatePresence mode="wait" data-id="element-174">
      <div className="fixed inset-0 z-[100000] flex items-center justify-center p-3 sm:p-6 md:p-8" data-id="element-175">
        {/* Backdrop */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 bg-black/92 backdrop-blur-md" data-id="element-176" />

        {step === 'upsell1' ? <motion.div key="upsell1" initial={{
        opacity: 0,
        scale: 0.85,
        y: 30
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: -20
      }} transition={{
        type: 'spring',
        damping: 22,
        stiffness: 280
      }} className="relative w-full max-w-[360px] sm:max-w-sm md:max-w-md z-10 rounded-3xl overflow-hidden" style={{
        boxShadow: '0 0 80px rgba(212,175,55,0.3), 0 30px 80px rgba(0,0,0,0.7)'
      }} data-id="element-177">
            {/* Gold border */}
            <div className="absolute inset-0 rounded-3xl bg-gradient-to-b from-gold via-gold/30 to-gold/50 p-[2px]" data-id="element-178">
              <div className="w-full h-full rounded-3xl bg-[#070707]" data-id="element-179" />
            </div>

            <div className="relative z-10" data-id="element-180">
              {/* Timer */}
              <div className="bg-red-600 text-white text-center py-2 flex items-center justify-center gap-2 text-[11px] sm:text-xs font-bold tracking-wider" data-id="element-181">
                <Clock className="w-3.5 h-3.5 animate-pulse" data-id="element-182" />
                OFERTA EXPIRA EN {formatTime(timeLeft)}
              </div>

              {/* Background image area with content overlay */}
              <div className="relative min-h-[180px] sm:min-h-[220px] md:min-h-[250px] flex items-end justify-center overflow-hidden" data-id="element-183">
                {/* BG Image */}
                <img src={UPSELL_IMG} alt="" aria-hidden="true" loading="lazy" className="absolute inset-0 w-full h-full object-cover object-center opacity-25 scale-110 blur-[1px]" data-id="element-184" />
                {/* Gradient overlays */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#070707] via-[#070707]/70 to-transparent" data-id="element-185" />
                <div className="absolute inset-0 bg-gradient-to-b from-[#070707]/40 to-transparent" data-id="element-186" />
                {/* Radial gold glow */}
                <div className="absolute inset-0 opacity-30" style={{
              background: 'radial-gradient(ellipse at 50% 40%, rgba(212,175,55,0.35) 0%, transparent 65%)'
            }} data-id="element-187" />

                {/* Floating content over image */}
                <div className="relative z-10 text-center px-5 pb-4 pt-8 sm:pt-10" data-id="element-188">
                  <div className="inline-block bg-gold/20 backdrop-blur-sm text-gold text-[9px] sm:text-[10px] font-black uppercase tracking-[3px] px-4 py-1.5 rounded-full mb-3 border border-gold/30" data-id="element-189">
                    ★ BONO EXCLUSIVO ★
                  </div>
                  <h3 className="font-serif text-2xl sm:text-3xl md:text-4xl font-black text-white leading-tight drop-shadow-lg" data-id="element-190">
                    El Código de
                    <br data-id="element-191" />
                    <span className="text-gold drop-shadow-[0_0_20px_rgba(212,175,55,0.4)]" data-id="element-192">
                      Riqueza Suprema
                    </span>
                  </h3>
                </div>
              </div>

              {/* Bottom content */}
              <div className="px-5 pb-5 sm:px-6 sm:pb-6 pt-3 text-center" data-id="element-193">
                <p className="text-white/50 text-xs sm:text-sm mb-4" data-id="element-194">
                  Lleva tu reprogramación al nivel más alto
                </p>

                {/* Price */}
                <div className="mb-4" data-id="element-195">
                  <span className="text-5xl sm:text-6xl font-black text-gold tracking-tight drop-shadow-[0_0_20px_rgba(212,175,55,0.3)]" data-id="element-196">
                    +$6.39
                  </span>
                  <div className="text-white/35 text-[11px] mt-1.5" data-id="element-197">
                    Se suma a tu orden · Total: $12.78
                  </div>
                </div>

                {/* CTA */}
                <motion.button onClick={handleAccept1} whileHover={{
              scale: 1.03
            }} whileTap={{
              scale: 0.97
            }} className="w-full bg-gold text-dark font-black text-base sm:text-lg py-4 sm:py-5 rounded-2xl shadow-[0_8px_35px_rgba(212,175,55,0.5)] flex items-center justify-center gap-2 uppercase tracking-wider" data-id="element-198">
                  AÑADIR A MI ORDEN
                  <ChevronRight className="w-5 h-5" data-id="element-199" />
                </motion.button>

                <button onClick={handleDecline1} className="mt-4 text-white/20 hover:text-white/40 text-[10px] sm:text-[11px] underline transition-colors tracking-wide" data-id="element-200">
                  No gracias, continuar sin el bono
                </button>
              </div>
            </div>
          </motion.div> : <motion.div key="upsell2" initial={{
        opacity: 0,
        scale: 0.85,
        y: 30
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: -20
      }} transition={{
        type: 'spring',
        damping: 22,
        stiffness: 280
      }} className="relative w-full max-w-[360px] sm:max-w-sm md:max-w-md z-10 rounded-3xl overflow-hidden" style={{
        boxShadow: '0 0 80px rgba(239,68,68,0.2), 0 30px 80px rgba(0,0,0,0.7)'
      }} data-id="element-201">
            {/* Red border */}
            <div className="absolute inset-0 rounded-3xl bg-gradient-to-b from-red-500 via-red-500/25 to-orange-500/30 p-[2px]" data-id="element-202">
              <div className="w-full h-full rounded-3xl bg-[#070707]" data-id="element-203" />
            </div>

            <div className="relative z-10" data-id="element-204">
              {/* Urgency banner */}
              <div className="bg-gradient-to-r from-red-600 via-red-500 to-orange-500 text-white text-center py-2.5 px-4" data-id="element-205">
                <div className="font-black text-xs sm:text-sm uppercase tracking-wider animate-pulse" data-id="element-206">
                  ⚡ ÚLTIMA OPORTUNIDAD ⚡
                </div>
              </div>

              {/* Background image area */}
              <div className="relative min-h-[180px] sm:min-h-[220px] md:min-h-[250px] flex items-end justify-center overflow-hidden" data-id="element-207">
                {/* BG Image */}
                <img src={DOWNSELL_IMG} alt="" aria-hidden="true" loading="lazy" className="absolute inset-0 w-full h-full object-cover object-center opacity-20 scale-110 blur-[1px]" data-id="element-208" />
                {/* Gradient overlays */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#070707] via-[#070707]/70 to-transparent" data-id="element-209" />
                <div className="absolute inset-0 bg-gradient-to-b from-[#070707]/40 to-transparent" data-id="element-210" />
                {/* Radial red glow */}
                <div className="absolute inset-0 opacity-25" style={{
              background: 'radial-gradient(ellipse at 50% 40%, rgba(239,68,68,0.3) 0%, transparent 65%)'
            }} data-id="element-211" />

                {/* Content over image */}
                <div className="relative z-10 text-center px-5 pb-4 pt-8 sm:pt-10" data-id="element-212">
                  <h3 className="font-serif text-2xl sm:text-3xl md:text-4xl font-black text-white leading-tight drop-shadow-lg" data-id="element-213">
                    ¡Espera! Toma un
                    <br data-id="element-214" />
                    <span className="text-green-400 drop-shadow-[0_0_15px_rgba(74,222,128,0.3)]" data-id="element-215">
                      30% OFF
                    </span>
                  </h3>
                  <p className="text-white/50 text-xs sm:text-sm mt-2" data-id="element-216">
                    El Código de Riqueza Suprema
                  </p>
                </div>
              </div>

              {/* Bottom content */}
              <div className="px-5 pb-5 sm:px-6 sm:pb-6 pt-3 text-center" data-id="element-217">
                {/* Price comparison */}
                <div className="mb-5" data-id="element-218">
                  <div className="flex items-center justify-center gap-3 mb-2" data-id="element-219">
                    <span className="text-xl text-white/20 line-through font-bold" data-id="element-220">
                      $6.39
                    </span>
                    <span className="text-5xl sm:text-6xl font-black text-green-400 tracking-tight drop-shadow-[0_0_20px_rgba(74,222,128,0.3)]" data-id="element-221">
                      $4.47
                    </span>
                  </div>
                  <div className="inline-block bg-green-400/15 text-green-400 text-[10px] font-black px-3 py-1 rounded-full uppercase tracking-wider border border-green-400/20" data-id="element-222">
                    AHORRAS $1.92
                  </div>
                  <div className="text-white/35 text-[11px] mt-2" data-id="element-223">
                    Total con descuento: $10.86
                  </div>
                </div>

                {/* CTA */}
                <motion.button onClick={handleAccept2} whileHover={{
              scale: 1.03
            }} whileTap={{
              scale: 0.97
            }} className="w-full bg-gradient-to-r from-green-500 to-emerald-400 text-dark font-black text-base sm:text-lg py-4 sm:py-5 rounded-2xl shadow-[0_8px_35px_rgba(74,222,128,0.35)] flex items-center justify-center gap-2 uppercase tracking-wider" data-id="element-224">
                  APLICAR DESCUENTO
                  <ChevronRight className="w-5 h-5" data-id="element-225" />
                </motion.button>

                <button onClick={handleDecline2} className="mt-4 text-white/20 hover:text-white/40 text-[10px] sm:text-[11px] underline transition-colors tracking-wide" data-id="element-226">
                  No gracias, ir al checkout sin bono
                </button>
              </div>
            </div>
          </motion.div>}
      </div>
    </AnimatePresence>;
}